/* global Image, createImageBitmap, URL, File */
// Client-side image downscale + recompression, run before any upload so we
// store smaller files (saves disk on the server's EFS volume and upload
// bandwidth). It is intentionally best-effort: any decode/encode failure, or a
// runtime without canvas support (jsdom under tests, very old browsers), falls
// straight through and returns the original File unchanged.

const DEFAULTS = {
  // Longest-edge cap. Photos from phones are routinely 4000px+; 1920 is plenty
  // for an in-thread attachment, and small square icons need far less.
  maxDimension: 1920,
  // JPEG/WebP quality. 0.82 is visually lossless for chat-sized images.
  quality: 0.82,
};

// Cache the one-time WebP-encode feature check.
let webpSupported = null;

function supportsWebp() {
  if (webpSupported !== null) return webpSupported;
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    webpSupported = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    webpSupported = false;
  }
  return webpSupported;
}

/**
 * Scale (w, h) so the longest edge is at most `max`, never upscaling.
 * Pure + exported so it can be unit-tested without a canvas.
 */
export function computeTargetDimensions(width, height, max) {
  if (!width || !height) return { width: width || 0, height: height || 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

function loadBitmap(file) {
  // Prefer createImageBitmap (fast, no DOM node); fall back to an <img> + object
  // URL for browsers that lack it.
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/**
 * Downscale and recompress an image File. Returns a new (smaller) File, or the
 * original File when resizing isn't possible or wouldn't help.
 *
 * Non-raster or animated images are passed through untouched: GIFs would lose
 * their animation and SVGs are vector, so re-encoding them is never a win.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<File>}
 */
export async function resizeImage(file, options = {}) {
  if (!file || typeof file.type !== 'string' || !file.type.startsWith('image/')) {
    return file;
  }
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  const { maxDimension, quality } = { ...DEFAULTS, ...options };

  let bitmap;
  try {
    bitmap = await loadBitmap(file);
  } catch {
    return file; // couldn't decode — upload the original
  }

  const width = bitmap.width;
  const height = bitmap.height;
  const target = computeTargetDimensions(width, height, maxDimension);

  let canvas;
  let ctx;
  try {
    canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx) {
    if (bitmap.close) bitmap.close();
    return file; // no canvas (e.g. jsdom) — upload the original
  }

  // Preserve transparency by encoding to WebP (alpha + far smaller than PNG).
  // When the browser can't encode WebP we fall back to JPEG, flattening any
  // alpha onto white — acceptable for the large images that benefit from this.
  const hasAlpha = file.type === 'image/png' || file.type === 'image/webp';
  const outType = hasAlpha && supportsWebp() ? 'image/webp' : 'image/jpeg';
  if (outType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
  }
  ctx.drawImage(bitmap, 0, 0, target.width, target.height);
  if (bitmap.close) bitmap.close();

  const blob = await canvasToBlob(canvas, outType, quality);
  if (!blob) return file;

  // If we neither shrank dimensions nor the byte size, keep the original so we
  // never make an already-tiny file larger by re-encoding it.
  if (target.scale >= 1 && blob.size >= file.size) return file;

  const ext = outType === 'image/webp' ? 'webp' : 'jpg';
  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${baseName}.${ext}`, {
    type: outType,
    lastModified: Date.now(),
  });
}

export default resizeImage;
